import { NumericFormat, type NumericFormatProps } from 'react-number-format';
import { forwardRef } from 'react';

import { DEFAULT_MODE_PROPS } from './NumberInput.constants';

type PartialInputEvent = { target: { name?: string; value: string } };

type Value = string | number | readonly string[] | undefined;

const DISPLAY_NAME = 'NumericFormatAdapter';
const USER_INPUT_SOURCE = 'event';

export type NumericFormatAdapterProps = {
  allowNegative?: boolean;
  className?: string;
  decimalScale?: number;
  decimalSeparator?: string;
  defaultValue?: Value;
  disabled?: boolean;
  fixedDecimalScale?: boolean;
  max?: number;
  min?: number;
  name?: string;
  onChange: (event: PartialInputEvent) => void;
  prefix?: string;
  readOnly?: boolean;
  thousandSeparator?: string | boolean;
  value?: Value;
  valueIsNumericString?: boolean;
};

const NumericFormatAdapter = forwardRef<
  HTMLInputElement,
  NumericFormatAdapterProps & NumericFormatProps
>(({ max, min, name, onChange, value, ...rest }, ref) => (
  <NumericFormat
    {...DEFAULT_MODE_PROPS}
    {...rest}
    getInputRef={ref}
    isAllowed={(values) => {
      const { floatValue } = values;

      if (floatValue == null) return true;

      const passesMax = max == null || floatValue <= max;
      const passesMin = min == null || floatValue >= min;

      return passesMax && passesMin;
    }}
    name={name}
    value={value}
    onValueChange={(values, e) => {
      // this also gets called when react-number-format receives a prop and reformats it
      // for controlled inputs, prevent the "echo" back
      if (value !== undefined && e.source !== USER_INPUT_SOURCE) return;

      onChange({
        target: {
          name,
          value: values.value,
        },
      });
    }}
  />
));

NumericFormatAdapter.displayName = DISPLAY_NAME;

export default NumericFormatAdapter;
